import type {
    AdaptiveInsights,
    EnhancedCategoryPerformance
} from '@/types/AdaptiveLearningTypes';

/**
 * ASWB Exam Category Code to Name Mapping.
 *
 * ⚠️ LAST-RESORT FALLBACK ONLY. This global map is exam-specific-WRONG: the same
 * code means different things per exam (e.g. LCSW "IIIA" ≠ LMSW "IIIA"), so a
 * single global name table cannot be authoritative. PREFER backend-provided
 * labels (from getAvailableCategories, which returns exam-scoped code→label
 * pairs) wherever a label is available, and fall back to this map only when the
 * backend label is missing. Kept in rough sync with backend categories.ts.
 */
export const CATEGORY_NAMES: Record<string, string> = {
    // Detailed subcategory codes (Roman numeral + letter)
    IA: 'Human Growth and Development',
    IB: 'Human Behavior in the Social Environment',
    IC: 'Diversity and Discrimination',
    IIA: 'Biopsychosocial History and Collateral Data',
    IIB: 'Assessment and Diagnosis',
    IIC: 'Treatment Planning',
    IIIA: 'Therapeutic Relationship',
    IIIB: 'Intervention Process',
    IIIC: 'Service Delivery and Management',
    IIID: 'Consultation and Collaboration',
    IVA: 'Professional Values and Ethics',
    IVB: 'Confidentiality',
    IVC: 'Professional Development and Use of Self',
    // Numeric content area codes (1-4)
    '1': 'Human Development & Behavior',
    '2': 'Assessment & Intervention Planning',
    '3': 'Interventions with Clients',
    '4': 'Professional Relationships & Ethics',
    // Roman numeral content area codes
    'I': 'Human Development & Behavior',
    'II': 'Assessment & Intervention Planning',
    'III': 'Interventions with Clients',
    'IV': 'Professional Relationships & Ethics',
};

/**
 * Get human-readable category name from code
 * Falls back to formatted code if mapping not found
 */
export function getCategoryDisplayName(categoryCode: string): string {
    // Check if it's a known code
    if (CATEGORY_NAMES[categoryCode]) {
        return CATEGORY_NAMES[categoryCode];
    }
    // Fallback: strip a leading roman-numeral code (e.g. "IA. ", "IIIB. "),
    // normalize separators, and title-case (mirrors iOS displayName(for:)).
    const stripped = categoryCode
        .replace(/^[IVXLCDM]+[A-Z]?\.\s*/i, '')
        .replace(/_/g, ' ')
        .trim();
    return (stripped || categoryCode)
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
}

export interface ProcessedCategoryData {
    name: string;
    score: number;
    questionsAttempted: number;
    trend: string;
    performance?: string;
}

export interface WeakCategory {
    category: string;
    successRate: number;
    questionsAttempted?: number;
    recommendedFocus?: string[];
}

/**
 * Process category data from various sources into a unified format
 */
export const processCategoryData = (
    categoryStrengths?: Record<string, EnhancedCategoryPerformance>,
    insights?: AdaptiveInsights | null
): ProcessedCategoryData[] => {
    // Use category strengths if available
    if (categoryStrengths && Object.keys(categoryStrengths).length > 0) {
        return Object.entries(categoryStrengths)
            .map(([category, performance]) => ({
                name: getCategoryDisplayName(category),
                score: performance.totalCount > 0
                    ? Math.round((performance.correctCount / performance.totalCount) * 100)
                    : 0,
                questionsAttempted: performance.totalCount || 0,
                trend: performance.improvement || 'steady',
                performance: performance.totalCount > 0
                    ? (performance.correctCount / performance.totalCount) >= 0.8
                        ? 'strong'
                        : (performance.correctCount / performance.totalCount) >= 0.6
                            ? 'good'
                            : 'needs_work'
                    : 'needs_work'
            }))
            .sort((a, b) => b.score - a.score);
    }

    // Use ALL categories from insights if available
    if (insights?.allCategories && insights.allCategories.length > 0) {
        return insights.allCategories
            .map(cat => ({
                name: getCategoryDisplayName(cat.category),
                score: Math.round(cat.successRate * 100),
                questionsAttempted: cat.questionsAttempted ?? 0,
                trend: 'steady',
                performance: cat.performance
            }))
            .sort((a, b) => b.score - a.score);
    }

    // Fallback to weak categories only
    if (insights?.weakCategories && insights.weakCategories.length > 0) {
        return insights.weakCategories.map(cat => ({
            name: getCategoryDisplayName(cat.category),
            score: Math.round(cat.successRate * 100),
            questionsAttempted: cat.questionsAttempted || 0,
            trend: 'steady',
            performance: 'needs_work'
        }));
    }

    return [];
};

/**
 * Extract weak categories that need attention
 */
export const getWeakCategories = (insights?: AdaptiveInsights | null): WeakCategory[] => {
    // Direct weak categories
    if (insights?.weakCategories && insights.weakCategories.length > 0) {
        return insights.weakCategories.map(cat => ({
            ...cat,
            questionsAttempted: cat.questionsAttempted || 0
        }));
    }
    
    // Extract from all categories if no dedicated weak categories
    if (insights?.allCategories && insights.allCategories.length > 0) {
        const needsWork = insights.allCategories.filter(cat => cat.successRate < 0.8);
        if (needsWork.length > 0) {
            return needsWork.map(cat => ({
                category: cat.category,
                successRate: cat.successRate,
                questionsAttempted: cat.questionsAttempted ?? 0
            }));
        }
    }
    
    return [];
};

/**
 * Get styling classes for score-based coloring
 */
export const getScoreColorClasses = (score: number) => ({
    background: score >= 80
        ? 'bg-success-50 border-success-200'
        : score >= 70
        ? 'bg-warning-50 border-warning-200'
        : 'bg-error-50 border-error-200',

    text: score >= 80
        ? 'text-success-700'
        : score >= 70
        ? 'text-warning-700'
        : 'text-error-700',

    progress: score >= 80 ? 'bg-success-500' : score >= 70 ? 'bg-warning-500' : 'bg-error-500'
});

/**
 * Get urgency level and styling for weak categories
 */
export const getUrgencyInfo = (successRate: number) => {
    const level = successRate < 0.6 ? 'high' : successRate < 0.7 ? 'medium' : 'low';

    const styles = {
        high: {
            background: 'bg-error-50 border-error-200',
            text: 'text-error-700',
            progress: 'bg-error-500',
            label: '🔥 High Priority'
        },
        medium: {
            background: 'bg-warning-50 border-warning-200',
            text: 'text-warning-700',
            progress: 'bg-warning-500',
            label: '⚡ Medium Priority'
        },
        low: {
            background: 'bg-primary-50 border-primary-200',
            text: 'text-primary-800',
            progress: 'bg-primary-500',
            label: '💡 Low Priority'
        }
    };

    return { level, ...styles[level] };
};

/**
 * Get trend icon based on performance trend
 */
export const getTrendIcon = (trend: string): string => {
    switch (trend) {
        case 'improving': return '📈';
        case 'declining': return '📉';
        default: return '➡️';
    }
};

/**
 * Get performance badge styling
 */
export const getPerformanceBadgeClasses = (performance: string): string => {
    switch (performance) {
        case 'strong':
            return 'bg-success-100 text-success-700';
        case 'good':
            return 'bg-primary-200 text-primary-800';
        default:
            return 'bg-secondary-200 text-secondary-800';
    }
};

/**
 * Get performance badge text
 */
export const getPerformanceBadgeText = (performance: string): string => {
    switch (performance) {
        case 'strong': return 'Strong';
        case 'good': return 'Good';
        default: return 'Practice More';
    }
};